import type { OnlineOrderStatus, OnlineOrderSummary } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';

const FILTERS: Array<OnlineOrderStatus | 'all'> = ['all', 'submitted', 'fulfilled', 'cancelled'];
const TONE: Record<OnlineOrderStatus, 'info' | 'success' | 'danger'> = {
  submitted: 'info',
  fulfilled: 'success',
  cancelled: 'danger',
};

export function CmsOrdersPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const [filter, setFilter] = useState<OnlineOrderStatus | 'all'>('all');
  const [orders, setOrders] = useState<OnlineOrderSummary[] | null>(null);

  useEffect(() => {
    setOrders(null);
    const params = filter === 'all' ? {} : { status: filter };
    void api().get<{ items: OnlineOrderSummary[] }>('/cms/orders', { params })
      .then((r) => setOrders(r.data.items))
      .catch(() => setOrders([]));
  }, [filter]);

  const money = (v: string, c: string): string => formatMoney(v, { locale: i18n.language, currency: c });

  const columns: DataTableColumn<OnlineOrderSummary>[] = [
    {
      id: 'orderNumber', header: t('orders.orderNumber'), sortValue: (r) => r.orderNumber, searchValue: (r) => `${r.orderNumber} ${r.customerName}`,
      cell: (r) => <Link to={`/cms/orders/${r.id}`} className="font-mono font-medium text-brand hover:underline">{r.orderNumber}</Link>,
    },
    { id: 'date', header: t('orders.date'), sortValue: (r) => r.date, cell: (r) => r.date },
    { id: 'customer', header: t('orders.customer'), sortValue: (r) => r.customerName, cell: (r) => r.customerName },
    {
      id: 'payment', header: t('orders.payment'), sortValue: (r) => `${r.fibPaid ? 1 : 0}${r.paymentMethod ?? ''}`,
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5">
          {r.paymentMethod ? t(`orders.payMethod.${r.paymentMethod}`, { defaultValue: r.paymentMethod }) : '—'}
          {/* FIB confirmed the money arrived — the one fact that stops a driver
              collecting the total a second time at the door. */}
          {r.fibPaid && <Badge variant="success">{t('orders.fibPaid', { defaultValue: 'Paid · FIB' })}</Badge>}
        </span>
      ),
    },
    {
      id: 'status', header: t('orders.status'), sortValue: (r) => r.status,
      cell: (r) => <Badge variant={TONE[r.status]}>{t(`orders.statusVal.${r.status}`, { defaultValue: r.status })}</Badge>,
    },
    { id: 'total', header: t('orders.total'), align: 'end', cellClassName: 'font-mono tabular-nums', sortValue: (r) => Number(r.total), cell: (r) => money(r.total, r.currencyCode) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('orders.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('orders.subtitle')}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs ${filter === f ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
          >
            {f === 'all' ? t('orders.filterAll') : t(`orders.statusVal.${f}`)}
          </button>
        ))}
      </div>

      {!orders ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : orders.length === 0 ? (
        <EmptyState title={t('orders.empty')} description={t('orders.emptyHint')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={orders} rowKey={(r) => r.id} searchPlaceholder={t('orders.search')} />
        </Card>
      )}
    </div>
  );
}
